import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { API } from "@/lib/api";
import { useT } from "@/lib/i18n";

const SESSION_KEY = "pstx_ai_session";

function getSessionId() {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
        sid = "s_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
        localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
}

export default function ChatWidget() {
    const location = useLocation();
    const { lang } = useT();
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [streaming, setStreaming] = useState(false);
    const sessionId = useRef(getSessionId());
    const scrollRef = useRef(null);
    const abortRef = useRef(null);

    const isAdmin = location.pathname.startsWith("/admin");

    const greeting = lang === "es"
        ? "¡Hola! Soy el asistente de PowerSaver TX. Pregúntame sobre planes de luz, cómo funciona la comparación o cómo registrarte."
        : "Hey! I'm the PowerSaver TX assistant. Ask me about Texas electricity plans, how comparison works, or how to sign up.";

    useEffect(() => {
        if (open && messages.length === 0) {
            setMessages([{ role: "assistant", text: greeting }]);
        }
    }, [open]); // eslint-disable-line

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, streaming]);

    if (isAdmin) return null;

    const send = async () => {
        const text = input.trim();
        if (!text || streaming) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
        setStreaming(true);

        try {
            abortRef.current = new AbortController();
            const resp = await fetch(`${API}/ai/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ session_id: sessionId.current, message: text, lang }),
                signal: abortRef.current.signal,
            });
            if (!resp.ok || !resp.body) {
                throw new Error("Chat unavailable");
            }
            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const parts = buffer.split("\n\n");
                buffer = parts.pop() || "";
                for (const part of parts) {
                    const line = part.trim();
                    if (!line.startsWith("data:")) continue;
                    const payload = line.slice(5).replace(/^ /, "");
                    if (payload === "[DONE]") continue;
                    setMessages((m) => {
                        const copy = [...m];
                        const last = copy[copy.length - 1];
                        if (last && last.role === "assistant") {
                            copy[copy.length - 1] = { ...last, text: last.text + payload };
                        }
                        return copy;
                    });
                }
            }
        } catch (e) {
            setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                if (last && last.role === "assistant" && !last.text) {
                    copy[copy.length - 1] = {
                        ...last,
                        text: lang === "es"
                            ? "No pude conectar. Intenta de nuevo o haz clic en Empezar."
                            : "I couldn't connect. Please try again or click Get Started.",
                    };
                }
                return copy;
            });
        } finally {
            setStreaming(false);
            abortRef.current = null;
        }
    };

    const onKey = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    return (
        <>
            {!open && (
                <button
                    data-testid="ai-chat-open-btn"
                    onClick={() => setOpen(true)}
                    className="fixed bottom-5 right-5 z-[60] group flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-full shadow-xl shadow-emerald-500/30 px-5 py-3.5 transition-all hover:scale-105"
                    aria-label="Open chat"
                >
                    <MessageCircle className="w-5 h-5" strokeWidth={2.5} />
                    <span className="hidden sm:inline text-sm">
                        {lang === "es" ? "Chatea con PowerSaver" : "Chat with us"}
                    </span>
                </button>
            )}
            {open && (
                <div
                    data-testid="ai-chat-panel"
                    className="fixed bottom-4 right-4 left-4 sm:left-auto sm:right-5 sm:bottom-5 z-[60] sm:w-[380px] h-[min(560px,80vh)] flex flex-col rounded-2xl overflow-hidden border border-emerald-500/20 bg-slate-900/95 backdrop-blur-xl shadow-2xl shadow-black/50"
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-gradient-to-r from-emerald-500/10 to-transparent">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                                <Sparkles className="w-4 h-4 text-emerald-400" />
                            </div>
                            <div>
                                <div className="text-sm font-bold text-white leading-tight">PowerSaver TX</div>
                                <div className="text-[11px] text-emerald-400/80 leading-tight">
                                    {lang === "es" ? "Asistente IA · En línea" : "AI Assistant · Online"}
                                </div>
                            </div>
                        </div>
                        <button
                            data-testid="ai-chat-close-btn"
                            onClick={() => setOpen(false)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition"
                            aria-label="Close chat"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="ai-chat-messages">
                        {messages.map((m, i) => (
                            <div
                                key={i}
                                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                            >
                                <div
                                    data-testid={`ai-msg-${m.role}`}
                                    className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                                        m.role === "user"
                                            ? "bg-emerald-500 text-slate-950 rounded-br-sm font-medium"
                                            : "bg-white/5 text-slate-100 rounded-bl-sm border border-white/5"
                                    }`}
                                >
                                    {m.text || (streaming && i === messages.length - 1 ? "…" : "")}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="border-t border-white/10 px-3 py-3 bg-slate-950/40">
                        <div className="flex items-end gap-2">
                            <textarea
                                data-testid="ai-chat-input"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={onKey}
                                rows={1}
                                placeholder={lang === "es" ? "Pregunta algo…" : "Ask anything…"}
                                className="flex-1 resize-none bg-white/5 text-sm text-white placeholder:text-slate-500 border border-white/10 focus:border-emerald-500/50 focus:outline-none rounded-xl px-3 py-2.5 max-h-28"
                                disabled={streaming}
                            />
                            <button
                                data-testid="ai-chat-send-btn"
                                onClick={send}
                                disabled={streaming || !input.trim()}
                                className="shrink-0 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-950 rounded-xl p-2.5 transition"
                                aria-label="Send"
                            >
                                <Send className="w-4 h-4" strokeWidth={2.5} />
                            </button>
                        </div>
                        <div className="text-[10px] text-slate-500 mt-2 px-1">
                            {lang === "es"
                                ? "Respuestas generadas por IA. Jeffrey confirma todas las tasas."
                                : "AI-generated responses. Jeffrey confirms all actual rates."}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
